"use client"
import { useState, useEffect, useMemo, useCallback, memo, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faInfoCircle,
  faHashtag,
  faCalendarAlt,
  faClipboardList,
  faUser,
  faClipboardQuestion,
  faFilter,
  faSearch,
  faMobile,
  faDesktop,
  faInbox,
  faSync,
  faServer,
   faWrench,
   faTicket,
   faTicketAlt,
  faRefresh,
  faClock as FaClockSolid,
} from "@fortawesome/free-solid-svg-icons"
import { FaCheckCircle, FaSpinner, FaListAlt, FaClock } from "react-icons/fa"
import { Form, Button, Card, Badge, Offcanvas, Toast, OverlayTrigger, Tooltip } from "react-bootstrap"
import { Link } from "react-router-dom"
import { getTicketCountsByAgentId, getTicketsByAgent, getInquiryByClientId } from "../../../services/AgentServices"
import Skeleton from "react-loading-skeleton"

const STORAGE_KEYS = {
  TICKET_UPDATE: "ticket_updated",
  LAST_REFRESH: "tickets_last_refresh",
  DATA_CHANGED: "tickets_data_changed",
  AUTO_REFRESH_ENABLED: "auto_refresh_enabled",
  REFRESH_INTERVAL: "refresh_interval",
}

const REFRESH_INTERVALS = {
  OFF: 0,
  FAST: 30000, // 30 seconds
  NORMAL: 60000, // 1 minute
  SLOW: 300000, // 5 minutes
}

// Helper function to extract and format follow-up date from complaints array
const extractFollowUpFromComplaints = (complaints) => {
  if (!Array.isArray(complaints) || complaints.length === 0) {
    return null
  }

  // Find the first complaint with a valid followup_date
  for (const complaint of complaints) {
    if (
      complaint.followup_date &&
      complaint.followup_date !== null &&
      complaint.followup_date !== "null" &&
      complaint.followup_date !== "" &&
      complaint.followup_date !== "0000-00-00" &&
      complaint.followup_date !== "0000-00-00 00:00:00"
    ) {
      // Format the date to show only date part (DD/MM/YYYY)
      try {
        const date = new Date(complaint.followup_date)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-GB") // Returns DD/MM/YYYY format
        }
      } catch (error) {
        console.error("Error formatting follow-up date:", error)
      }
    }
  }

  return null
}

// Helper function to format date without time
const formatDateOnly = (dateString) => {
  if (!dateString) return "—"
  try {
    const date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("en-GB") // Returns DD/MM/YYYY format
    }
  } catch (error) {
    console.error("Error formatting date:", error)
  }
  return "—"
}

// Helper function to get relative time for better UX
const getRelativeTime = (dateString) => {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

    if (diffInHours < 1) return "Just now"
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 48) return "Yesterday"
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return formatDateOnly(dateString)
  } catch (error) {
    return formatDateOnly(dateString)
  }
}

const processComplaintDisplay = (ticket) => {
  const complaints = ticket.complaints || []

  // Check if this is an RMS ticket
  const isRMS = complaints.some((complaint) => {
    const complaintText =
      typeof complaint === "object"
        ? complaint.complaint_detail || complaint.complaint_description || ""
        : complaint || ""
    return complaintText.toLowerCase().includes("rms")
  })

  if (isRMS) {
    // For RMS tickets, show only "RMS Schedule Request" once
    return {
      type: "RMS",
      displayText: "RMS Schedule Request",
      count: 1,
    }
  } else {
    // For regular complaints, show all unique complaints
    const uniqueComplaints = [
      ...new Set(
        complaints.map((complaint) =>
          typeof complaint === "object"
            ? complaint.complaint_detail || complaint.complaint_description || "No description"
            : complaint || "No description",
        ),
      ),
    ]

    return {
      type: "Non-RMS",
      displayText: uniqueComplaints,
      count: uniqueComplaints.length,
    }
  }
}

const TicketCard = memo(({ ticket, getStatusDescription, getBadgeVariant, showScheduled, showStatus }) => {
  const followUpDate = extractFollowUpFromComplaints(ticket.complaints || [])
  const complaintProcessed = processComplaintDisplay(ticket)

  return (
    <Card className="mb-3 border-0 shadow-sm ticket-card-modern">
      <Card.Header className="bg-gradient-light border-0 py-3">
        <div className="d-flex justify-content-between align-items-center">
          <Link
            to={`/agent/components/Userconversation?ticketId=${ticket.ticket_id}`}
            state={{ status: ticket.status }}
            className="text-decoration-none fw-bold text-primary ticket-link-modern"
          >
            <div className="d-flex align-items-center">
              <div className="ticket-id-badge me-2">
                <FontAwesomeIcon icon={faHashtag} className="me-1" />
                {ticket.type === "inquiry" ? "CBR-" : "CBR-"}{ticket.ticket_id}
              </div>
            </div>
          </Link>
          {showStatus && (
            <Badge bg={getBadgeVariant(ticket.status)} className="px-3 py-2 status-badge-modern">
              {getStatusDescription(ticket.status)}
            </Badge>
          )}
        </div>
      </Card.Header>
      <Card.Body className="py-4">
        <div className="row g-3">
          <div className="col-12">
            <div className="info-section">
              <div className="info-label">
                <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                Complainant
              </div>
              <div className="info-value">{ticket.complaintname || "Unknown"}</div>
            </div>
          </div>

          <div className="col-12">
            <div className="info-section">
              <div className="info-label">
                <FontAwesomeIcon icon={faClipboardQuestion} className="me-2 text-warning" />
                Complaint
              </div>
              <div className="info-value">
                {complaintProcessed.type === "RMS" ? (
                  <div className="rms-complaint">
                    <div className="fw-semibold text-info d-flex align-items-center">
                      <FontAwesomeIcon icon={faServer} className="me-2" />
                      RMS Schedule Request
                    </div>
                  </div>
                ) : (
                  <div className="complaint-tags">
                    {Array.isArray(complaintProcessed.displayText) && complaintProcessed.displayText.length > 0 ? (
                      complaintProcessed.displayText.map((complaint, idx) => (
                        <Badge key={idx} bg="light" text="dark" className="me-2 mb-2 complaint-tag">
                          {complaint}
                        </Badge>
                      ))
                    ) : (
                      <Badge bg="light" text="dark" className="complaint-tag">
                        No Complaint
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="info-section">
              <div className="info-label">
                <FontAwesomeIcon icon={faClipboardList} className="me-2 text-info" />
                Remarks
              </div>
              <div className="info-value remarks-text">
                {ticket.activity_description || ticket.description || "No Remark"}
              </div>
            </div>
          </div>

          <div className="row mt-3">
            {showScheduled && (
              <div className="col-6">
                <div className="info-section">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-success" />
                    Scheduled
                  </div>
                  <div className="info-value">
                    {followUpDate ? (
                      <span className="text-success fw-medium scheduled-date">{followUpDate}</span>
                    ) : (
                      <span className="text-muted">Not Scheduled</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className={showScheduled ? "col-6" : "col-12"}>
              <div className="info-section">
                <div className="info-label">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-secondary" />
                  Received
                </div>
                <div className="info-value">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {ticket.created_at || ticket.createdAt
                          ? new Date(ticket.created_at || ticket.createdAt).toLocaleString("en-GB")
                          : "Unknown"}
                      </Tooltip>
                    }
                  >
                    <span className="text-secondary received-date">
                      {getRelativeTime(ticket.created_at || ticket.createdAt)}
                    </span>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
})

TicketCard.displayName = "TicketCard"

export default function Tickets({ type = "ticket" }) {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filterStatus, setFilterStatus] = useState("Open")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [dateFilter, setDateFilter] = useState("All")
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState("table")
  const [lastUpdated, setLastUpdated] = useState(null)
  const [showUpdateToast, setShowUpdateToast] = useState(false)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.AUTO_REFRESH_ENABLED) === "true"
  })
  const [refreshInterval, setRefreshInterval] = useState(() => {
    return Number.parseInt(localStorage.getItem(STORAGE_KEYS.REFRESH_INTERVAL)) || REFRESH_INTERVALS.NORMAL
  })
  const [statusCounts, setStatusCounts] = useState({
    All: 0,
    Open: 0,
    "In-Progress": 0,
    Resolved: 0,
    "No-Response": 0,
  })

  // Refs for event handling
  const isComponentMounted = useRef(true)
  const lastDataHash = useRef(null)
  const autoRefreshTimer = useRef(null)

  // Memoized constants
  const clientId = useMemo(() => sessionStorage.getItem("clientId"), [])
  const userId = useMemo(() => sessionStorage.getItem("userId"), [])
  const pageSize = 10
  const statusKeywords = useMemo(
    () => ({
      All: ["all", "OPN", "INP", "CRS", "CNR"],
      Open: ["open", "OPN"],
      "In-Progress": ["in progress", "INP"],
      Resolved: ["resolved", "CRS"],
      "No-Response": ["no response", "CNR"],
    }),
    [],
  )

  const statusIcons = useMemo(
    () => ({
      All: <FaListAlt />,
      Open: <FontAwesomeIcon icon={faInbox} />,
      "In-Progress": <FaSpinner className="fa-spin" />,
      Resolved: <FaCheckCircle />,
      "No-Response": <FaClock />,
    }),
    [],
  )

  const statusColors = useMemo(
    () => ({
      All: "primary",
      Open: "success",
      "In-Progress": "warning",
      Resolved: "info",
      "No-Response": "danger",
    }),
    [],
  )

  // Enhanced status descriptions for better UX
  const statusDescriptions = useMemo(
    () => ({
      All: type === "inquiry" ? "All Inquiries" : "All Tickets",
      Open: type === "inquiry" ? "New Inquiries" : "New Tickets",
      "In-Progress": type === "inquiry" ? "Active Inquiries" : "Active Tickets",
      Resolved: "Completed",
      "No-Response": "Closed - No Response",
    }),
    [type],
  )

  // Memoized helper functions
  const getStatusDescription = useCallback((code) => {
    switch (code?.toUpperCase()) {
      case "OPN":
        return "Open"
      case "INP":
        return "In-Progress"
      case "CRS":
        return "Resolved"
      case "CNR":
        return "No-Response"
      default:
        return code || "Unknown"
    }
  }, [])

  const getBadgeVariant = useCallback((status) => {
    const code = status?.toUpperCase()
    if (code === "OPN") return "success"
    if (code === "INP") return "warning"
    if (code === "CRS") return "info"
    if (code === "CNR") return "danger"
    return "secondary"
  }, [])

  const createDataHash = useCallback((data) => {
    return JSON.stringify(data)
      .split("")
      .reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0)
        return a & a
      }, 0)
  }, [])

  // Enhanced data fetching with better error handling
  const fetchData = useCallback(
    async (showRefreshIndicator = false) => {
      if (showRefreshIndicator) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }

      try {
        console.log(`🔄 Fetching ${type} data...`)
        let ticketsRes = null;

        if (type === "inquiry") {
          const inquiryRes = await getInquiryByClientId(clientId, 1, 0);
          if (inquiryRes && inquiryRes.success) {
            // Filter by assigned agent locally
            const agentInqs = (inquiryRes.data || []).filter(inq => 
              [...String(inq.agent_remarks || "").matchAll(/\[user_id:(\d+)\]/gi)]
                .map((match) => Number.parseInt(match[1], 10))
                .filter((value) => Number.isFinite(value))
                .pop() === Number.parseInt(userId, 10) ||
              inq.assigned_to === Number.parseInt(userId, 10)
            );

            // Remap inquiry list to match ticket list structure
            const mappedInquiries = agentInqs.map(inq => ({
              type: "inquiry",
              ticket_id: inq.id,
              status: inq.status_code || "OPN",
              complaintname: inq.Client_name,
              created_at: inq.created_at,
              createdAt: inq.created_at,
              activity_description: inq.agent_remarks,
              description: inq.agent_remarks,
              complaints: inq.last_question ? [{
                 complaint_detail: inq.last_question.text,
                 followup_date: inq.Next_followup
              }] : [],
            }));
            ticketsRes = { success: true, tickets: mappedInquiries };
          }
        } else {
          ticketsRes = await getTicketsByAgent(clientId, userId)
        }

        console.log(`📊 Raw ${type} response:`, ticketsRes)

        if (ticketsRes?.success && Array.isArray(ticketsRes.tickets)) {
          // Process items - keep the structure as is from backend
          const processedTickets = ticketsRes.tickets.map((ticket) => {
            // Extract follow-up date from complaints array
            const followUpDate = extractFollowUpFromComplaints(ticket.complaints || [])

            return {
              ...ticket,
              // Keep original data structure
              description: ticket.activity_description || ticket.description || "No Remark",
              created_at: ticket.createdAt || ticket.created_at,
              // Store extracted follow-up date for easy access
              extractedFollowUpDate: followUpDate,
            }
          })

          console.log("✅ Processed tickets:", processedTickets.length)

          // Log tickets with follow-up dates
          const ticketsWithFollowUp = processedTickets.filter((t) => t.extractedFollowUpDate)
          console.log(`📅 Tickets with follow-up dates: ${ticketsWithFollowUp.length}`)
          ticketsWithFollowUp.forEach((ticket) => {
            console.log(`  Ticket ${ticket.ticket_id}: ${ticket.extractedFollowUpDate}`)
          })

          // Check if data has changed using hash comparison
          const newDataHash = createDataHash(processedTickets)
          const hasDataChanged = newDataHash !== lastDataHash.current

          if (hasDataChanged) {
            setTickets(processedTickets)
            setLastUpdated(new Date())
            lastDataHash.current = newDataHash

            // Store last refresh time
            localStorage.setItem(STORAGE_KEYS.LAST_REFRESH, Date.now().toString())

            if (showRefreshIndicator) {
              setShowUpdateToast(true)
              setTimeout(() => setShowUpdateToast(false), 3000)
            }
          } else if (showRefreshIndicator) {
            console.log("ℹ️ No data changes detected")
          }
        } else {
          console.error("❌ Invalid tickets response:", ticketsRes)
          setTickets([])
        }
      } catch (err) {
        console.error("❌ Error fetching tickets:", err)
        setTickets([])
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    },
    [clientId, userId, createDataHash],
  )

  const fetchStatusCounts = useCallback(async () => {
    try {
      console.log(`🔄 Fetching ${type} counts...`)
      if (type === "inquiry") {
        // Compute from current list if simple, since getting inquiries gets them all
        const inquiryRes = await getInquiryByClientId(clientId, 1, 0);
        if (inquiryRes && inquiryRes.success) {
           const agentInqs = (inquiryRes.data || []).filter(inq => 
              [...String(inq.agent_remarks || "").matchAll(/\[user_id:(\d+)\]/gi)]
                .map((match) => Number.parseInt(match[1], 10))
                .filter((value) => Number.isFinite(value))
                .pop() === Number.parseInt(userId, 10) ||
              inq.assigned_to === Number.parseInt(userId, 10)
           );
           const o = agentInqs.filter(i => (i.status_code || "OPN") === "OPN").length;
           const p = agentInqs.filter(i => i.status_code === "INP").length;
           const r = agentInqs.filter(i => i.status_code === "CRS").length;
           const nr = agentInqs.filter(i => i.status_code === "CNR").length;
           setStatusCounts({
             All: o + p + r + nr,
             Open: o,
             "In-Progress": p,
             Resolved: r,
             "No-Response": nr,
           });
        }
      } else {
        const result = await getTicketCountsByAgentId(userId)
        const counts = result?.data || {}
        setStatusCounts({
          All:
            (counts.totalCount ?? 0) ||
            (counts.openCount || 0) +
              (counts.inProgressCount || 0) +
              (counts.closedCount || 0) +
              (counts.noResponseCount || 0),
          Open: counts.openCount || 0,
          "In-Progress": counts.inProgressCount || 0,
          Resolved: counts.closedCount || 0,
          "No-Response": counts.noResponseCount || 0,
        })
      }
    } catch (err) {
      console.error(`❌ Failed to fetch ${type} counts`, err)
    }
  }, [userId, clientId, type])

  // Manual refresh function
  const handleManualRefresh = useCallback(async () => {
    console.log("🔄 Manual refresh triggered")
    await Promise.all([fetchData(true), fetchStatusCounts()])
  }, [fetchData, fetchStatusCounts])

  const startAutoRefresh = useCallback(() => {
    if (autoRefreshTimer.current) {
      clearInterval(autoRefreshTimer.current)
    }

    if (autoRefreshEnabled && refreshInterval > 0) {
      autoRefreshTimer.current = setInterval(() => {
        if (isComponentMounted.current) {
          console.log("🔄 Auto-refreshing tickets...")
          handleManualRefresh()
        }
      }, refreshInterval)
    }
  }, [autoRefreshEnabled, refreshInterval, handleManualRefresh])

  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshTimer.current) {
      clearInterval(autoRefreshTimer.current)
      autoRefreshTimer.current = null
    }
  }, [])

  const toggleAutoRefresh = useCallback(() => {
    const newEnabled = !autoRefreshEnabled
    setAutoRefreshEnabled(newEnabled)
    localStorage.setItem(STORAGE_KEYS.AUTO_REFRESH_ENABLED, newEnabled.toString())

    if (newEnabled) {
      startAutoRefresh()
    } else {
      stopAutoRefresh()
    }
  }, [autoRefreshEnabled, startAutoRefresh, stopAutoRefresh])

  const handleRefreshIntervalChange = useCallback(
    (newInterval) => {
      setRefreshInterval(newInterval)
      localStorage.setItem(STORAGE_KEYS.REFRESH_INTERVAL, newInterval.toString())

      if (autoRefreshEnabled) {
        startAutoRefresh()
      }
    },
    [autoRefreshEnabled, startAutoRefresh],
  )

  // Event-driven refresh system
  useEffect(() => {
    // Listen for storage events (data changes from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEYS.TICKET_UPDATE || e.key === STORAGE_KEYS.DATA_CHANGED) {
        console.log("🔄 Storage event detected - refreshing data")
        handleManualRefresh()
      }
    }

    // Listen for window focus (user comes back to tab)
    const handleWindowFocus = () => {
      const lastRefresh = localStorage.getItem(STORAGE_KEYS.LAST_REFRESH)
      const timeSinceLastRefresh = Date.now() - (Number.parseInt(lastRefresh) || 0)

      // Refresh if it's been more than 2 minutes since last refresh
      if (timeSinceLastRefresh > 2 * 60 * 1000) {
        console.log("🔄 Window focus detected - refreshing data")
        handleManualRefresh()
      }
    }

    // Listen for visibility change (tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const lastRefresh = localStorage.getItem(STORAGE_KEYS.LAST_REFRESH)
        const timeSinceLastRefresh = Date.now() - (Number.parseInt(lastRefresh) || 0)

        // Refresh if it's been more than 1 minute since last refresh
        if (timeSinceLastRefresh > 60 * 1000) {
          console.log("🔄 Tab visibility change detected - refreshing data")
          handleManualRefresh()
        }
      }
    }

    // Add event listeners
    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("focus", handleWindowFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("focus", handleWindowFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [handleManualRefresh])

  // Function to trigger data refresh from other components
  const triggerDataRefresh = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.DATA_CHANGED, Date.now().toString())
    handleManualRefresh()
  }, [handleManualRefresh])

  // Expose refresh function globally for other components to use
  useEffect(() => {
    window.refreshTicketsData = triggerDataRefresh
    return () => {
      delete window.refreshTicketsData
    }
  }, [triggerDataRefresh])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isComponentMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (autoRefreshEnabled) {
      startAutoRefresh()
    }

    return () => {
      stopAutoRefresh()
    }
  }, [autoRefreshEnabled, refreshInterval, startAutoRefresh, stopAutoRefresh])

  // Memoized date filter function
  const isWithinDateFilter = useCallback(
    (createdDate) => {
      if (!createdDate) return false
      const date = new Date(createdDate)
      const today = new Date()

      if (fromDate && toDate) {
        const from = new Date(fromDate)
        const to = new Date(toDate)
        to.setHours(23, 59, 59, 999)
        return date >= from && date <= to
      }

      switch (dateFilter) {
        case "Today":
          return date.toDateString() === today.toDateString()
        case "ThisWeek": {
          const startOfWeek = new Date(today)
          startOfWeek.setDate(today.getDate() - today.getDay())
          const endOfWeek = new Date(startOfWeek)
          endOfWeek.setDate(startOfWeek.getDate() + 6)
          return date >= startOfWeek && date <= endOfWeek
        }
        case "ThisMonth":
          return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
        case "ThisYear":
          return date.getFullYear() === today.getFullYear()
        default:
          return true
      }
    },
    [fromDate, toDate, dateFilter],
  )

  // Memoized filtered tickets
  const filteredTickets = useMemo(() => {
    return tickets
      .filter((ticket) => {
        const status = ticket.status?.toUpperCase() || ""
        if (filterStatus === "All") return true
        return statusKeywords[filterStatus]?.includes(status)
      })
      .filter(
        (ticket) =>
          (ticket.complaintname || "").toLowerCase().includes((window.searchQuery || "").toLowerCase()) ||
          (ticket.ticket_id ? ticket.ticket_id.toString().includes(window.searchQuery) : false),
      )
      .filter((ticket) => dateFilter === "All" || isWithinDateFilter(ticket.created_at))
  }, [tickets, filterStatus, statusKeywords, window.searchQuery, dateFilter, isWithinDateFilter])

  // Memoized pagination
  const { totalPages, paginatedTickets } = useMemo(() => {
    const total = Math.ceil(filteredTickets.length / pageSize)
    const paginated = filteredTickets.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    return { totalPages: total, paginatedTickets: paginated }
  }, [filteredTickets, currentPage, pageSize])

  // Optimized event handlers
  const handleFilter = useCallback(() => {
    if (!fromDate || !toDate) {
      alert("Please select both From and To date.")
      return
    }
    if (new Date(fromDate) > new Date(toDate)) {
      alert("From date cannot be after To date.")
      return
    }
    setDateFilter("manual")
    setShowFilters(false)
  }, [fromDate, toDate])

  const handleReset = useCallback(() => {
    setFromDate("")
    setToDate("")
    setDateFilter("All")
    setFilterStatus("Open")
    setShowFilters(false)
    setCurrentPage(1)
  }, [])

  const handleStatusFilter = useCallback((status) => {
    setFilterStatus(status)
    setCurrentPage(1)
  }, [])

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode)
  }, [])

  // Initial data loading - ONLY ONCE
  useEffect(() => {
    const loadData = async () => {
      if (clientId && userId) {
        console.log("🚀 Initial data loading...")
        await Promise.all([fetchData(false), fetchStatusCounts()])
      }
    }

    loadData()

    // Detect mobile and set view mode
    const isMobile = window.innerWidth < 768
    setViewMode(isMobile ? "cards" : "table")
  }, [clientId, userId]) // Only depend on clientId and userId

  // Enhanced table rows with conditional columns
  const tableRows = useMemo(() => {
    return paginatedTickets.map((ticket) => {
      // Extract follow-up date from complaints array
      const followUpDate = extractFollowUpFromComplaints(ticket.complaints || [])

      const complaintProcessed = processComplaintDisplay(ticket)

      return (
        <tr key={ticket.ticket_id} className="border-bottom table-row-hover">
          <td className="fw-bold">
            <Link
              to={`/agent/components/Userconversation?${type === 'inquiry' ? 'inquiryId' : 'ticketId'}=${ticket.ticket_id}&type=${type}`}
              state={{ status: ticket.status }}
              className="text-decoration-none fw-bold text-primary ticket-link"
            >
              <FontAwesomeIcon icon={faHashtag} className="me-1" />
              CBR-{ticket.ticket_id}
            </Link>
          </td>
          <td>
            <div className="complaint-cell">
              {complaintProcessed.type === "RMS" ? (
                <div>
                  <div className="fw-normal text-muted d-flex align-items-center">
                    <FontAwesomeIcon icon={faWrench} className="me-1" />
                    RMS Schedule Request
                    
                  </div>
                </div>
              ) : (
                <div>
                  {Array.isArray(complaintProcessed.displayText) && complaintProcessed.displayText.length > 0 ? (
                    <div>
                      {complaintProcessed.displayText.slice(0, 1).map((complaint, idx) => (
                        <div key={idx} className="complaint-item mb-1">
                         
                          <div className="fw-normal text-muted d-block">  <FontAwesomeIcon icon={faTicket} className="me-1" />{complaint}</div>
                        </div>
                      ))}
                      {complaintProcessed.displayText.length > 1 && (
                        <small className="text-info">
                          +{complaintProcessed.displayText.length - 1} more complaints
                        </small>
                      )}
                    </div>
                  ) : (
                    <small className="text-muted">No complaint details</small>
                  )}
                </div>
              )}
            </div>
          </td>
                                <td className="border-0 py-2 px-3">
      {[
  ...new Set(ticket.complaints.map((c) => c.device_id)) // unique IDs
]
  .map((id) => (id === 0 || id === "0" ? "New Customer Enquiry" : id))
  .join(", ")}
          </td>
          
         
          {filterStatus === "All" && (
            <td>
              <Badge bg={getBadgeVariant(ticket.status)} className="px-2 py-1 status-badge">
                {getStatusDescription(ticket.status)}
              </Badge>
            </td>
          )}
          <td>
            <div className="small text-break text-secondary" style={{ maxWidth: "200px" }}>
              {ticket.activity_description || ticket.description || "No Remark"}
            </div>
          </td>
          {filterStatus === "All" ||
            (filterStatus === "In-Progress" && (
              <td className="text-nowrap">
                {followUpDate ? (
                  <span className="text-success fw-medium">{followUpDate}</span>
                ) : (
                  <span className="text-muted">Not Scheduled</span>
                )}
              </td>
            ))}
          <td className="text-nowrap">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {ticket.created_at || ticket.createdAt
                    ? new Date(ticket.created_at || ticket.createdAt).toLocaleString("en-GB")
                    : "Unknown"}
                </Tooltip>
              }
            >
              <span className="text-secondary">{formatDateOnly(ticket.created_at || ticket.createdAt)}</span>
            </OverlayTrigger>
          </td>
        </tr>
      )
    })
  }, [paginatedTickets, getBadgeVariant, getStatusDescription, filterStatus])

  // Memoized pagination component
  const paginationComponent = useMemo(() => {
    if (totalPages <= 1) return null

    return (
      <nav className="mt-4">
        <ul className="pagination justify-content-center flex-wrap">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
              Previous
            </button>
          </li>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
            let pageNum
            if (totalPages <= 5) {
              pageNum = index + 1
            } else if (currentPage <= 3) {
              pageNum = index + 1
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + index
            } else {
              pageNum = currentPage - 2 + index
            }
            return (
              <li key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                  {pageNum}
                </button>
              </li>
            )
          })}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    )
  }, [totalPages, currentPage])

  return (
    <div>
      <style jsx>{`
        .ticket-card-modern {
          transition: all 0.3s ease;
          border-radius: 12px !important;
          overflow: hidden;
        }
        
        .ticket-card-modern:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
        
        .bg-gradient-light {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }
        
        .ticket-id-badge {
          background: linear-gradient(135deg, #007bff, #0056b3);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }
        
        .ticket-link-modern:hover .ticket-id-badge {
          background: linear-gradient(135deg, #0056b3, #004085);
          transform: scale(1.05);
          transition: all 0.2s ease;
        }
        
        .status-badge-modern {
          border-radius: 20px !important;
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        
        .info-section {
          margin-bottom: 1rem;
        }
        
        .info-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: #6c757d;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .info-value {
          font-size: 0.95rem;
          color: #212529;
          line-height: 1.4;
        }
        
        .complaint-tag {
          border-radius: 15px !important;
          font-size: 0.8rem;
          padding: 4px 12px !important;
          border: 1px solid #dee2e6 !important;
        }
        
        .remarks-text {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 8px;
          border-left: 4px solid #007bff;
          font-size: 0.9rem;
        }
        
        .rms-complaint {
          background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
          padding: 12px;
          border-radius: 8px;
          border-left: 4px solid #2196f3;
        }
        
        .scheduled-date {
          background: #d4edda;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.85rem;
        }
        
        .received-date {
          background: #f8f9fa;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.85rem;
        }
        
        .status-filter-modern {
          border-radius: 25px !important;
          padding: 8px 20px !important;
          font-weight: 600;
          border: 2px solid transparent !important;
          transition: all 0.3s ease;
        }
        
        .status-filter-modern.active {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .refresh-controls {
          background: linear-gradient(135deg, #f8f9fa, #e9ecef);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
          border: 1px solid #dee2e6;
        }
        
        .auto-refresh-indicator {
          display: inline-flex;
          align-items: center;
          background: #d4edda;
          color: #155724;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }
        
        .auto-refresh-indicator.disabled {
          background: #f8d7da;
          color: #721c24;
        }
        
        .table-modern {
          border-radius: 12px !important;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .table-modern thead th {
          background:  #a6b6c5ff !important;
          border: none !important;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table-modern tbody tr {
          transition: all 0.2s ease;
        }
        
        .table-modern tbody tr:hover {
          background: linear-gradient(135deg, #f8f9fa, #e9ecef) !important;
          transform: scale(1.01);
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1 fw-bold text-dark">
            <FontAwesomeIcon icon={faInbox} className="me-2 text-primary" />
            {type === "inquiry" ? "Inquiry Management" : "Ticket Management"}
          </h4>
          <div className="d-flex align-items-center">
            {lastUpdated && <small className="text-muted me-3">Last updated: {lastUpdated.toLocaleTimeString()}</small>}
            <div className={`auto-refresh-indicator ${!autoRefreshEnabled ? "disabled" : ""}`}>
              <FontAwesomeIcon
                icon={autoRefreshEnabled ? faSync : FaClockSolid}
                className={`me-1 ${autoRefreshEnabled ? "fa-spin" : ""}`}
              />
              {autoRefreshEnabled ? "Auto-refresh ON" : "Auto-refresh OFF"}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant={refreshing ? "secondary" : "primary"}
            size="sm"
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="d-flex align-items-center"
          >
            <FontAwesomeIcon icon={faRefresh} className={`me-2 ${refreshing ? "fa-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowFilters(true)}
            className="d-flex align-items-center"
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filters
          </Button>

          <div className="d-flex">
            <Button
              variant={viewMode === "table" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handleViewModeChange("table")}
              className="me-1"
            >
              <FontAwesomeIcon icon={faDesktop} />
            </Button>
            <Button
              variant={viewMode === "cards" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handleViewModeChange("cards")}
            >
              <FontAwesomeIcon icon={faMobile} />
            </Button>
          </div>
        </div>
      </div>

      <div className="refresh-controls">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="auto-refresh-switch"
                label="Enable Auto-refresh"
                checked={autoRefreshEnabled}
                onChange={toggleAutoRefresh}
                className="me-3"
              />
              {autoRefreshEnabled && (
                <small className="text-success">
                  <FontAwesomeIcon icon={faSync} className="fa-spin me-1" />
                  Active
                </small>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <Form.Label className="me-2 mb-0 small">Interval:</Form.Label>
              <Form.Select
                size="sm"
                value={refreshInterval}
                onChange={(e) => handleRefreshIntervalChange(Number.parseInt(e.target.value))}
                disabled={!autoRefreshEnabled}
                style={{ width: "auto" }}
              >
                <option value={REFRESH_INTERVALS.FAST}>30 seconds</option>
                <option value={REFRESH_INTERVALS.NORMAL}>1 minute</option>
                <option value={REFRESH_INTERVALS.SLOW}>5 minutes</option>
              </Form.Select>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex flex-wrap gap-2">
          {Object.keys(statusCounts).map((status) => (
            <Button
              key={status}
              variant={filterStatus === status ? statusColors[status] : "outline-secondary"}
              size="sm"
              onClick={() => handleStatusFilter(status)}
              className={`status-filter-modern ${filterStatus === status ? "active" : ""}`}
            >
              <span className="me-2">{statusIcons[status]}</span>
              {statusDescriptions[status]}
              <Badge
                bg={filterStatus === status ? "light" : statusColors[status]}
                text={filterStatus === status ? "dark" : "white"}
                className="ms-2"
              >
                {statusCounts[status]}
              </Badge>
            </Button>
          ))}
        </div>
      </div>

      {/* Desktop Date Filters */}
      <Card className="mb-4 d-none d-md-block">
        <Card.Body className="py-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <Form.Label className="small text-muted mb-1">From Date</Form.Label>
              <Form.Control type="date" size="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <Form.Label className="small text-muted mb-1">To Date</Form.Label>
              <Form.Control type="date" size="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <Button size="sm" onClick={handleFilter} className="w-100">
                <FontAwesomeIcon icon={faSearch} className="me-1" />
                Filter
              </Button>
            </div>
            <div className="col-md-2">
              <Button variant="outline-secondary" size="sm" onClick={handleReset} className="w-100">
                Reset
              </Button>
            </div>
            <div className="col-md-3">
              <Form.Label className="small text-muted mb-1">Quick Filter</Form.Label>
              <Form.Select size="sm" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                <option value="All">All Time</option>
                <option value="Today">Today</option>
                <option value="ThisWeek">This Week</option>
                <option value="ThisMonth">This Month</option>
                <option value="ThisYear">This Year</option>
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Enhanced Filters Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
        <Offcanvas.Header closeButton className="bg-primary text-white">
          <Offcanvas.Title>
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Advanced Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-4">
            <Form.Label className="fw-bold">Date Range</Form.Label>
            <div className="mb-3">
              <Form.Label className="small text-muted">From Date</Form.Label>
              <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="mb-3">
              <Form.Label className="small text-muted">To Date</Form.Label>
              <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>

          <div className="mb-4">
            <Form.Label className="fw-bold">Quick Filters</Form.Label>
            <Form.Select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              <option value="All">All Time</option>
              <option value="Today">Today</option>
              <option value="ThisWeek">This Week</option>
              <option value="ThisMonth">This Month</option>
              <option value="ThisYear">This Year</option>
            </Form.Select>
          </div>

          <div className="d-grid gap-2">
            <Button variant="primary" onClick={handleFilter} className="fw-bold">
              <FontAwesomeIcon icon={faSearch} className="me-2" />
              Apply Filters
            </Button>
            <Button variant="outline-secondary" onClick={handleReset}>
              Reset All Filters
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Content */}
      {loading ? (
        <div className="row">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="col-12 mb-3">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Skeleton height={120} className="mb-2" />
                  <Skeleton height={20} width="60%" className="mb-2" />
                  <Skeleton height={20} width="40%" />
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      ) : filteredTickets.length > 0 ? (
        <>
          {/* Enhanced Table View */}
          {viewMode === "table" && (
            <div className="table-responsive">
              <table className="table table-hover align-middle bg-white table-modern">
                <thead>
                  <tr>
                    <th className="border-0 py-3">
                      <FontAwesomeIcon icon={faHashtag} className="me-1" />
                      Ticket ID
                    </th>
                    <th className="border-0 py-3">
                      <FontAwesomeIcon icon={faClipboardQuestion} className="me-2" />
                      Ticket Details
                    </th>
                      <th className="border-0 py-3">
                      <FontAwesomeIcon icon={faTicket} className="me-1" />
                      Sticker IDs
                    </th>
                   
                    {filterStatus === "All" && (
                      <th className="border-0 py-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Status
                      </th>
                    )}
                    <th className="border-0 py-3">
                      <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                      Remarks
                    </th>
                    {filterStatus === "All" ||
                      (filterStatus === "In-Progress" && (
                        <th className="border-0 py-3">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                          Scheduled
                        </th>
                      ))}
                    <th className="border-0 py-3">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                      Received
                    </th>
                  </tr>
                </thead>
                <tbody>{tableRows}</tbody>
              </table>
            </div>
          )}

          {/* Enhanced Card View */}
          {viewMode === "cards" && (
            <div className="row">
              {paginatedTickets.map((ticket) => (
                <div key={ticket.ticket_id} className="col-12 col-lg-6 col-xl-4">
                  <TicketCard
                    ticket={ticket}
                    getStatusDescription={getStatusDescription}
                    getBadgeVariant={getBadgeVariant}
                    showScheduled={filterStatus === "All" || filterStatus === "In-Progress"}
                    showStatus={filterStatus === "All"}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Enhanced Pagination */}
          {paginationComponent}
        </>
      ) : (
        <Card className="text-center py-5 border-0 shadow-sm">
          <Card.Body>
            <div className="mb-4">
              <FontAwesomeIcon icon={faInbox} size="4x" className="text-muted opacity-50" />
            </div>
            <h5 className="text-muted fw-bold">No tickets found</h5>
            <p className="text-muted mb-4">
              {filterStatus !== "All"
                ? `No ${filterStatus.toLowerCase()} tickets match your current filters.`
                : "No tickets match your current filters."}
            </p>
            <Button variant="outline-primary" onClick={handleReset}>
              <FontAwesomeIcon icon={faRefresh} className="me-2" />
              Reset Filters
            </Button>
          </Card.Body>
        </Card>
      )}

      <Toast
        show={showUpdateToast}
        onClose={() => setShowUpdateToast(false)}
        delay={3000}
        autohide
        className="position-fixed bottom-0 end-0 m-3"
        style={{ zIndex: 1050 }}
      >
        <Toast.Header className="bg-success text-white">
          <FontAwesomeIcon icon={faSync} className="me-2" />
          <strong className="me-auto">Data Updated</strong>
        </Toast.Header>
        <Toast.Body>Tickets have been refreshed with the latest data.</Toast.Body>
      </Toast>
    </div>
  )
}
